import { ReactNode } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";

import { wacToken } from "@/libraries/design/foundations";
import { experiments, useExperiment } from "@/utils/experiment";

export type headerProps = {
    readonly mainHeadingFirstPart: string;
    readonly mainHeadingSecondPart: string;
    readonly subHeading: string;
    readonly testGWP7617Experiment: boolean;
};

export const LoggedOutHeader = ({
    subHeading,
    testGWP7617Experiment
}: headerProps) => {
    const { formatMessage } = useIntl();

    const gwp7617Experiment = useExperiment(
        experiments.gwp7617JiraShortTermAiInfusionExperiment
    );

    const useGWP7617Experiment =
        testGWP7617Experiment ||
        (gwp7617Experiment != undefined &&
            gwp7617Experiment.type === "enrolled" &&
            gwp7617Experiment.value === "test");

    const mainHeading = useGWP7617Experiment
        ? formatMessage(
              {
                  id: "jira-product-tour.hero-header.header-text-gwp-7617",
                  defaultMessage:
                      "<strong>Focus <br></br>on <span>outcomes,</span></strong><br></br> not admin"
              },
              {
                  strong: (text: ReactNode) => <strong>{text}</strong>,
                  span: (text: ReactNode) => <span>{text}</span>,
                  br: () => <br />
              }
          )
        : formatMessage(
              {
                  id: "jira-product-tour.hero-header.header-text",
                  defaultMessage:
                      "<strong>Great outcomes</strong> start with <em>Jira</em>"
              },
              {
                  strong: (text: ReactNode) => <strong>{text}</strong>,
                  em: (text: ReactNode) => <em>{text}</em>
              }
          );

    const displayedSubheading = useGWP7617Experiment
        ? formatMessage({
              id: "jira-product-tour.hero-header.subheading-text-gwp-7617",
              defaultMessage:
                  "AI-powered project management that removes the work around work."
          })
        : subHeading;

    return (
        <div css={sectionStyles} data-testid="logged-out-header">
            <h1 css={mainHeadingStyles}>{mainHeading}</h1>
            <div css={subheadingStyles}>{displayedSubheading}</div>
        </div>
    );
};

const sectionStyles = css({
    display: "grid",
    maxWidth: "480px",
    justifyItems: "start",
    [media.below.lg]: {
        paddingBlockStart: "32px"
    },
    [media.below.sm]: {
        maxWidth: "304px",
        justifyItems: "center"
    }
});

const subheadingStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontWeight: "400",
    fontSize: "20px",
    lineHeight: "28px",
    color: wacToken("color.primary.black"),
    [media.below.md]: {
        fontSize: "16px",
        lineHeight: "26px"
    },
    [media.below.sm]: {
        textAlign: "center"
    }
});

const mainHeadingStyles = css({
    color: wacToken("color.primary.black"),
    fontSize: "36px",
    fontWeight: "normal",
    width: "100%",
    lineHeight: 1.1,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    br: {
        display: "none"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    strong: {
        fontWeight: "bold",
        display: "block"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    em: {
        fontStyle: "normal",
        position: "relative",
        [":after"]: {
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='93' height='15' fill='none'%3E%3Cpath fill='%23FCA700' d='M93 12.932c-1.99-3.316.348-9.177-2.864-12.344-6.496-2.118-7.694 10.2-11.93 11.8-1.927-3.112.112-7.825-2.313-11.07.116.078.49.488.53.542-6.37-7.096-8.484 9.605-13.305 10.591C61.095 9.036 64.51-.225 58.305.17c-3.913.058-8.41 16.61-10.67 10.972l-.008.079-.029-.029c.22 4.017 3.69-.444 4.311-1.704-5.314 9.94-5.02-2.611-5.562-7.17 1.06 1.205.621 4.517.982 5.943-.05-17.405-9.074-2.114-13.194 4.017-2.922-.15-.804-7.23-2.102-9.554.075.497.564 3.311.083 3.859-.128-2.06-.012-6.417-3.166-5.89.323-.676 1.906 0 2.089.31-6.608-3.17-7.482 10.305-12.651 11.486-1.505-3.506.402-7.991-1.704-11.444C10.562-5.58 3.904 22.022 2.781 8.546 2.437 5.827 3.436 1.064 0 .596c.543.32 1.758 1.177 1.691 2.512-.091-.141-.178-.282-.27-.415.933 3.553-.638 7.358 1.025 10.703C6.76 17.504 11.1-.104 15.474 1.657c1.799 2.28.51 6.044.966 8.771-.034-.087-.2-.65-.228-.725 1.198 13.074 9.645-5.696 8.82-2.898 10.616-16.812.336 16.792 11.839 5.82 2.71-2.275 4.377-11.01 7.954-9.845.481 4.502.717 17.46 7.354 9.078 2.5-2.806 3.494-8.77 7.192-9.6 1.96 3.52-1.335 15.806 6.271 11.826-.074.03-.145.062-.224.091 3.084-1.077 6.222-15.56 9.455-11.196 1.248 2.836-1.102 10.997 3.706 11.242 4.207-.352 8.51-18.247 11.474-10.114.539 1.94.298 10.512 2.939 8.812l.008.013Z'/%3E%3C/svg%3E")`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "contain",
            content: "''",
            height: "20px",
            insetBlockEnd: -14,
            insetInlineEnd: -2,
            insetInlineStart: -4,
            position: "absolute"
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    span: {
        position: "relative",
        [":after"]: {
            backgroundImage: `url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" version="1.2" viewBox="0 0 252 26"><path d="m30 6.3c5.5-0.8 10.1 0.9 12.1 4.4 0.9 1.5 1.1 3 1.8 5.1 1.1 3.3 5.3 0.7 10.3-2.9 5.2-3.8 11.4-8.4 15.8-10.8 2.7-1.4 6.4-1.7 11.1-0.8 5.7 1.1 10.7 12.7 12.4 12.7 4.9 0 17.6-9.2 22.1-12.3 1.6-1.2 6.9-1.6 10.8-0.6 4 1.1 5.9 7.4 9.3 8.4 3.6 1 20.9-4.1 36.3-3.5 5.3 0.2 28.7 3.2 35.1 3.3 34 0.6 42.1-4.6 44-1.9 2 2.9-2.6 6-14.7 8-11.3 1.8-31 1.7-44.9 1.1-13.9-0.7-17.8-1.1-27.7-1.1-9.8 0-23 2.6-28.4 2.5-6.8-0.2-9.4-8.7-12.6-9.4-4.3-0.9-18 14.7-29.7 12.7-9.6-1.6-10.2-13.1-16.8-13.4-6-0.2-23.2 18-32.8 17.1-7.5-0.7-5.8-10-8.5-10.9-5.6-1.9-18.5 7.2-23.9 6.9-5.4-0.3-9.3-3.7-9.1-6.9 0.1-1.5 0.4-3.7 3-2.6 2.5 1.1 0.8 4.3 6.3 2.6 7.9-2.6 14-7.1 18.7-7.7z" fill="%23ab64ec"/></svg>')`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "contain",
            content: "''",
            height: "15px",
            insetBlockEnd: -6,
            insetInlineEnd: 20,
            insetInlineStart: 4,
            position: "absolute"
        }
    },
    [media.above.sm]: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        strong: {
            display: "inline"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        br: {
            display: "block"
        }
    },
    [media.only.md]: {
        fontSize: "48px",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ["em:after"]: {
            insetBlockEnd: -10
        }
    },
    [media.above.lg]: {
        fontSize: "66px",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ["em:after"]: {
            insetBlockEnd: -6
        }
    },
    [media.below.sm]: {
        textAlign: "center"
    }
});
